function normalize(v) {
  return (v ?? '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function applyBusinessRules(tariff, context) {
  const { activeSegment, activeMobileGroup, activeMyHomeTech } = context;

  if (activeSegment === 'mobile' && activeMobileGroup === 'kombi') {
    if (tariff.segment === 'mobile' && tariff.group === 'classic') {
      const mapped = { ...tariff };
      mapped.originalPrice = mapped.price;
      const discountedHalf = Math.floor(mapped.price * 50) / 100;
      mapped.price = discountedHalf.toFixed(2);
      mapped.isComboDiscounted = true;
      mapped.comboDiscount = '50%';
      mapped.title = `${mapped.title} KV`;

      const titleStr = (mapped.title || '').toLowerCase();
      const isMobileLOrAbove = titleStr.includes('mobile l') || titleStr.includes('mobile xl') || titleStr.includes('unlimited');
      if (isMobileLOrAbove) {
        mapped.connectionFee = 0;
      }

      return mapped;
    }
    return tariff;
  }

  if (activeSegment === 'myhome' && activeMyHomeTech === 'kombi') {
    const mapped = { ...tariff };
    mapped.originalPrice = mapped.price;
    mapped.price = (parseFloat(mapped.price) - 10).toFixed(2);
    mapped.isComboDiscounted = true;
    mapped.comboDiscount = '10€';
    mapped.title = `${mapped.title} KV`;
    return mapped;
  }

  if (activeSegment === 'kombivorteil' && tariff.segment === 'kombivorteil') {
    const mapped = { ...tariff };
    mapped.isComboDiscounted = true;
    if (mapped.promoPrice != null && mapped.promoPrice < mapped.price) {
      mapped.originalPrice = mapped.kombiOriginalPrice || mapped.price;
      mapped.price = mapped.promoPrice;
    }

    const homeGroups = ['vdsl', 'kabel', 'glasfaser', 'homespot'];
    if (homeGroups.includes(mapped.group)) {
      mapped.segment = 'myhome';
    } else if (mapped.group === 'mobile') {
      mapped.segment = 'mobile';
    }
    return mapped;
  }

  const mapped = { ...tariff };
  if (mapped.segment === 'mobile' && mapped.group === 'young') {
    mapped.connectionFee = 0;
  }
  return mapped;
}

function isIncludedByFilters(tariff, context) {
  const { searchQuery, activeSegment, activeMobileGroup, activeMyHomeTech, activeBrand } = context;

  if ((searchQuery || '').trim()) {
    const q = normalize(searchQuery);
    return (
      normalize(tariff.title).includes(q) ||
      normalize(tariff.category).includes(q) ||
      normalize(tariff.dataLabel).includes(q) ||
      (Array.isArray(tariff.features) && tariff.features.some((feature) => normalize(feature).includes(q))) ||
      (tariff.price != null && tariff.price.toString().includes(q))
    );
  }

  if (activeBrand === 'o2') {
    const tt = normalize(tariff.title || '');
    const tc = normalize(tariff.category || '');
    const tg = normalize(tariff.group || '');
    if (!(tt.includes('o2') || tc.includes('o2') || tg.includes('blue'))) return false;
  }

  if (activeBrand === 'superselect') {
    const tt = normalize(tariff.title || '');
    const tc = normalize(tariff.category || '');
    const tg = normalize(tariff.group || '');
    if (!(tt.includes('super') || tc.includes('super') || tg.includes('superselect') || tt.includes('select'))) return false;
  }

  if (activeSegment === 'kombivorteil') {
    return tariff.segment === 'kombivorteil';
  }

  if (activeSegment === 'mobile' && activeMobileGroup === 'alle') {
    return tariff.segment === 'mobile' || tariff.segment === 'blue';
  }

  if (activeSegment === 'mobile' && activeMobileGroup === 'kombi') {
    return tariff.segment === 'mobile' && tariff.group === 'classic';
  }

  if (tariff.segment !== activeSegment) return false;

  if (activeSegment === 'mobile') {
    return tariff.group === activeMobileGroup;
  }

  if (activeSegment === 'myhome') {
    if (activeMyHomeTech === 'kombi' || activeMyHomeTech === 'all') return true;
    return tariff.group === activeMyHomeTech;
  }

  return true;
}

function sortTariffs(a, b) {
  const groupOrder = { promo: 0, classic: 1, young: 2, '60plus': 3, business: 4, mobile: 5, vdsl: 6, kabel: 7, glasfaser: 8, homespot: 9 };
  const titleOrder = {
    'O2 Mobile S': 0,
    'O2 Mobile M': 1,
    'O2 Mobile L': 2,
    'O2 Mobile XL': 3,
    'O2 Mobile Unlimited Smart': 4,
    'O2 Mobile Unlimited on Demand': 5,
    'O2 Mobile Unlimited Max': 6,
    'O2 Mobile S Young': 10,
    'O2 Mobile M Young': 11,
    'O2 Mobile L Young': 12,
    'O2 Mobile XL Young': 13,
    'O2 Mobile Unlimited Smart Young': 14,
    'O2 Mobile Unlimited on Demand Young': 15,
    'O2 Mobile Unlimited Max Young': 16,
    'O2 Mobile S 60+': 20,
    'O2 Mobile M 60+': 21,
    'O2 Mobile L 60+': 22,
    'O2 Mobile XL 60+': 23,
    'O2 Mobile Unlimited Smart 60+': 24,
    'O2 Mobile Unlimited on Demand 60+': 25,
    'O2 Mobile Unlimited Max 60+': 26,
    'O2 Prepaid Basic': 0,
    'O2 Prepaid S': 1,
    'O2 Prepaid M': 2,
    'O2 Prepaid L': 3,
    'O2 Prepaid Max': 4
  };

  const groupOrderDiff = (groupOrder[a.group] ?? 99) - (groupOrder[b.group] ?? 99);
  if (groupOrderDiff !== 0) return groupOrderDiff;

  if ((a.segment === 'mobile' && b.segment === 'mobile') || (a.segment === 'prepaid' && b.segment === 'prepaid')) {
    const aOrder = titleOrder[a.title] ?? 50;
    const bOrder = titleOrder[b.title] ?? 50;
    if (aOrder !== bOrder) return aOrder - bOrder;
  }

  const extractNumeric = (label) => {
    if (typeof label === 'number') return label;
    if (label === 'unbegrenzt') return 1000;
    const match = String(label).match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 9999;
  };

  return extractNumeric(a.dataLabel) - extractNumeric(b.dataLabel);
}

export function getFilteredTariffs(tariffs, context) {
  return tariffs
    .filter((tariff) => isIncludedByFilters(tariff, context))
    .map((tariff) => applyBusinessRules(tariff, context))
    .sort(sortTariffs);
}
